import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';

interface Setup2FAProps {
  onBack: () => void;
  onComplete: () => void;
  userEmail: string;
  forceNew?: boolean; // Yeni secret üretmeyi zorla (2FA sıfırlandıktan sonra)
}

export default function Setup2FA({ onBack, onComplete, userEmail, forceNew = false }: Setup2FAProps) {
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verify
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // State'leri sıfırla (yeni kurulum için)
    setSecret('');
    setQrCodeUrl('');
    setIsLoading(true);
    
    // Önce backend'den secret'ı kontrol et
    checkBackendSecret();
  }, [userEmail]);

  // QR kod yoksa ve secret varsa, QR kod oluştur
  useEffect(() => {
    if (secret && !qrCodeUrl && !isLoading) {
      console.log('Secret var ama QR kod yok, QR kod oluşturuluyor...');
      try {
        const { authenticator } = require('@otplib/preset-default');
        const qrUrl = authenticator.keyuri(userEmail, 'PawleyClub Admin', secret);
        if (qrUrl) {
          setQrCodeUrl(qrUrl);
          console.log('QR kod başarıyla oluşturuldu');
        }
      } catch (error) {
        console.error('QR kod oluşturma hatası:', error);
      }
    }
  }, [secret, qrCodeUrl, isLoading, userEmail]);

  const checkBackendSecret = async () => {
    setIsLoading(true);
    
    // Eğer forceNew true ise, direkt yeni secret üret (2FA sıfırlandıktan sonra)
    if (forceNew) {
      console.log('2FA sıfırlandı, yeni secret üretiliyor...');
      generateSecret();
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      // Token yoksa, yeni secret üret
      console.log('Token yok, yeni secret üretiliyor...');
      generateSecret();
      setIsLoading(false);
      return;
    }

    try {
      // Backend'den 2FA durumunu kontrol et
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_2FA_STATUS}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.secret) {
          // Backend'de secret var, onu kullan
          console.log('Backend\'den secret alındı');
          setSecret(data.secret);
          const { authenticator } = require('@otplib/preset-default');
          const qrUrl = authenticator.keyuri(userEmail, 'PawleyClub Admin', data.secret);
          console.log('QR URL oluşturuldu:', qrUrl ? 'Başarılı' : 'Başarısız');
          setQrCodeUrl(qrUrl);
          setIsLoading(false);
          return;
        }
      }
    } catch (error) {
      // Backend hatası - fallback olarak localStorage kontrol et
      console.log('Backend 2FA endpoint bulunamadı, localStorage kullanılıyor:', error);
    }

    // Backend'de secret yoksa, localStorage'dan kontrol et
    const existingSecret = localStorage.getItem(`twoFactorSecret_${userEmail}`);
    if (existingSecret) {
      console.log('localStorage\'dan secret bulundu');
      setSecret(existingSecret);
      const { authenticator } = require('@otplib/preset-default');
      const qrUrl = authenticator.keyuri(userEmail, 'PawleyClub Admin', existingSecret);
      console.log('QR URL oluşturuldu:', qrUrl ? 'Başarılı' : 'Başarısız');
      setQrCodeUrl(qrUrl);
      setIsLoading(false);
    } else {
      // Hiç secret yoksa, yeni üret
      console.log('Secret bulunamadı, yeni secret üretiliyor...');
      generateSecret();
    }
  };

  const generateSecret = () => {
    try {
      // Generate real secret using otplib
      const { authenticator } = require('@otplib/preset-default');
      
      const secret = authenticator.generateSecret();
      const qrUrl = authenticator.keyuri(userEmail, 'PawleyClub Admin', secret);
      
      console.log('Yeni secret üretildi:', secret ? 'Başarılı' : 'Başarısız');
      console.log('QR URL oluşturuldu:', qrUrl ? 'Başarılı' : 'Başarısız');
      
      setSecret(secret);
      setQrCodeUrl(qrUrl);
      setIsLoading(false);
    } catch (error) {
      console.error('Secret üretme hatası:', error);
      setError('QR kod oluşturulamadı. Lütfen sayfayı yenileyin.');
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Lütfen 6 haneli kodu girin');
      return;
    }

    setIsVerifying(true);
    setError('');

    // Real 2FA verification using otplib
    const { authenticator } = require('@otplib/preset-default');
    
    try {
      // GitHub gibi çalışması için time window tolerance ekle
      // window: 2 = önceki 2 window ve sonraki 2 window'u da kabul et
      const verified = authenticator.verify({
        token: verificationCode,
        secret: secret,
        window: 2 // Önceki ve sonraki 2 window'u da kabul et (saat senkronizasyon sorunlarını önler)
      });

      setTimeout(() => {
        if (verified) {
          // Directly complete setup after verification
          completeSetup();
        } else {
          setError('Geçersiz kod. Lütfen Authenticator uygulamanızdan gelen güncel 6 haneli kodu girin. Kod her 30 saniyede değişir.');
        }
        setIsVerifying(false);
      }, 1000);
    } catch (error) {
      setTimeout(() => {
        setError('Kod doğrulama hatası. Lütfen tekrar deneyin.');
        setIsVerifying(false);
      }, 1000);
    }
  };

  const completeSetup = async () => {
    const token = localStorage.getItem('adminToken');
    let backendSaved = false;
    
    // Backend'e secret'ı kaydet
    if (token) {
      try {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.STAFF_2FA_SETUP}`, {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify({ secret }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Backend'e kaydedildi
            backendSaved = true;
            console.log('✅ 2FA secret backend\'e kaydedildi');
          } else {
            console.warn('⚠️ Backend 2FA setup başarısız:', data.message);
          }
        } else {
          const errorText = await response.text();
          console.warn('⚠️ Backend 2FA endpoint hatası:', response.status, errorText);
        }
      } catch (error) {
        // Backend endpoint yok veya erişilemiyor
        console.error('❌ Backend 2FA endpoint bulunamadı veya erişilemiyor:', error);
        console.warn('⚠️ Secret sadece bu browser\'da saklanacak. Farklı browser\'lardan giriş yapmak için backend endpoint\'lerini oluşturun.');
      }
    }
    
    // Her durumda localStorage'a da kaydet (fallback için)
    localStorage.setItem(`twoFactorSecret_${userEmail}`, secret);
    localStorage.removeItem('emailLoginCode');
    localStorage.removeItem('emailLoginCodeExpiry');
    
    if (!backendSaved) {
      // Backend'e kaydedilemediyse kullanıcıyı uyar
      alert('⚠️ UYARI: 2FA secret backend\'e kaydedilemedi. Bu nedenle sadece bu browser\'dan giriş yapabilirsiniz.\n\nFarklı browser\'lardan giriş yapmak için backend\'de 2FA endpoint\'lerini oluşturun:\n- POST /api/staff/2fa/setup\n- GET /api/staff/2fa/status\n- POST /api/staff/2fa/verify');
    }
    
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 my-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Geri
          </button>
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-blue-600 mr-2" />
            <h1 className="text-xl font-bold text-gray-900">2FA Kurulumu</h1>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center space-x-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              1
            </div>
            <div className={`w-16 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              2
            </div>
          </div>
        </div>

        {/* Step 1: QR Code */}
        {step === 1 && (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Google Authenticator Kurulumu
            </h2>
            <p className="text-gray-600 mb-4">
              Telefonunuza Google Authenticator (veya benzeri bir Authenticator uygulaması) indirin ve QR kodu okutun. <strong>Bu işlemi sadece bir kez yapacaksınız.</strong>
            </p>
            
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-4 flex justify-center items-center min-h-[250px]">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">QR kod oluşturuluyor...</p>
                </div>
              ) : qrCodeUrl ? (
                <QRCodeSVG value={qrCodeUrl} size={200} />
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                  <p className="text-red-600 mb-4">QR kod oluşturulamadı</p>
                  <button
                    onClick={() => {
                      setIsLoading(true);
                      generateSecret();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Tekrar Dene
                  </button>
                </div>
              )}
            </div>
            
            <details className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
              <summary className="font-semibold text-blue-900 mb-2 cursor-pointer hover:text-blue-700">
                Adımlar (tıklayarak açın)
              </summary>
              <ol className="text-sm text-blue-800 space-y-1 mt-2">
                <li>1. Google Authenticator (veya Authy, Microsoft Authenticator) uygulamasını indirin</li>
                <li>2. "+" butonuna tıklayın</li>
                <li>3. "QR kod tarayın" seçin</li>
                <li>4. Yukarıdaki QR kodu okutun</li>
                <li>5. <strong>Önemli:</strong> Bu QR kodu sadece bir kez okutacaksınız, sonraki girişlerde Authenticator'dan gelen kodu kullanacaksınız</li>
              </ol>
            </details>
            
            <button
              onClick={() => setStep(2)}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
            >
              QR Kodu Okuttum
            </button>
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 2 && (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Kod Doğrulama
            </h2>
            <p className="text-gray-600 mb-4">
              Authenticator uygulamanızdan gelen 6 haneli kodu girin.
            </p>
            
            <div className="mb-4">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full text-center text-2xl font-mono tracking-widest py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={6}
                autoFocus
              />
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {error}
                </div>
              </div>
            )}
            
            <button
              onClick={verifyCode}
              disabled={isVerifying || verificationCode.length !== 6}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-all shadow-lg hover:shadow-xl"
            >
              {isVerifying ? 'Doğrulanıyor...' : 'Doğrula'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
